import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, Upload, FileText } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProposalModal({ lead, onClose, onSuccess }) {
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === 'super_admin';
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [pricingSuggestion, setPricingSuggestion] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [formData, setFormData] = useState({
    line_items: [],
    discount_type: 'none',
    discount_value: 0,
    terms_and_conditions: ''
  });
  // Additional manual discount (additive to auto-discount from pricing rules)
  const [additionalDiscount, setAdditionalDiscount] = useState({
    type: 'none', // 'none', 'percentage', 'fixed'
    value: 0
  });
  const [newLineItem, setNewLineItem] = useState({
    service_id: '',
    service_name: '',
    description: '',
    deliverables: [],
    quantity: 1,
    rate: 0,
    amount: 0
  });

  useEffect(() => {
    fetchServices();
    fetchDefaultTerms();
  }, []);

  // Auto-populate line item from lead data once services are loaded
  useEffect(() => {
    if (services.length > 0 && formData.line_items.length === 0) {
      // Find matching service
      let matchingService = services.find(s => s.id === lead.service_type);
      if (!matchingService) {
        matchingService = services.find(s => s.title === lead.service_type);
      }
      
      // Use service base_rate as default (will be overridden by pricing rule if exists)
      const baseRate = matchingService?.base_rate || 0;
      const quantity = lead.num_applicants || 1;
      
      // Create initial line item from lead data
      const initialLineItem = {
        id: Date.now().toString(),
        service_id: matchingService?.id || '',
        service_name: matchingService?.title || lead.service_type || 'Visa Service',
        description: `${matchingService?.title || lead.service_type} for ${lead.display_country || lead.destination_country}`,
        deliverables: matchingService?.deliverables || [],
        quantity: quantity,
        rate: baseRate,
        amount: baseRate * quantity
      };
      
      // Add to form data
      setFormData(prev => ({
        ...prev,
        line_items: [initialLineItem]
      }));
    }
  }, [services.length]);
  
  // Fetch pricing suggestion after line item is created
  useEffect(() => {
    if (formData.line_items.length > 0 && !pricingSuggestion && !loadingPricing) {
      fetchPricingSuggestion(false);
    }
  }, [formData.line_items.length]);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/services`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setServices(data.filter(s => s.is_active));
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchDefaultTerms = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/company-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          terms_and_conditions: data.proposal_terms || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  const fetchPricingSuggestion = async (applyDiscount = false) => {
    if (!lead || !lead.id) return;
    
    try {
      setLoadingPricing(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/pricing-rules/suggest/${lead.id}?apply_discount=${applyDiscount}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      
      setPricingSuggestion(response.data);
      setDiscountApplied(applyDiscount && response.data.discount_applied);
      
      // Update existing line item with pricing from rules
      if (response.data && formData.line_items.length > 0) {
        const lineItemAmount = response.data.visaasure_charges_total;
        const ratePerApplicant = lineItemAmount / (lead.num_applicants || 1);
        
        // Update the first line item with pricing rule data
        setFormData(prev => ({
          ...prev,
          line_items: prev.line_items.map((item, index) => {
            if (index === 0) {
              return {
                ...item,
                rate: ratePerApplicant,
                amount: lineItemAmount
              };
            }
            return item;
          }),
          // Set discount if applied
          discount_type: (applyDiscount && response.data.discount_applied && response.data.discount_amount) ? 'fixed' : prev.discount_type,
          discount_value: (applyDiscount && response.data.discount_applied && response.data.discount_amount) ? response.data.discount_amount : prev.discount_value
        }));
      }
    } catch (error) {
      // Check if it's a 404 (no pricing rule found) - this is expected for services without rules
      if (error.response?.status === 404) {
        console.log('ℹ️ No pricing rule found for this service - manual pricing entry mode');
        setPricingSuggestion(null); // Clear any existing suggestion
        // Line item already exists from auto-population, user can edit rate manually
      } else {
        // Only log as error if it's not a 404
        console.error('Error fetching pricing suggestion:', error);
      }
    } finally {
      setLoadingPricing(false);
    }
  };

  const handleApplyDiscount = async () => {
    await fetchPricingSuggestion(true);
  };

  const handleServiceSelect = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setNewLineItem({
        service_id: service.id,
        service_name: service.title,
        description: service.description,
        deliverables: [...(service.deliverables || [])],
        quantity: 1,
        rate: service.base_rate || 0,
        amount: service.base_rate || 0
      });
    }
  };

  const calculateLineItemAmount = (quantity, rate) => {
    return quantity * rate;
  };

  const addLineItem = () => {
    if (newLineItem.service_name && newLineItem.rate > 0) {
      // Check if a line item with the same service already exists
      const existingItem = formData.line_items.find(
        item => item.service_name === newLineItem.service_name && 
                item.description === newLineItem.description
      );
      
      if (existingItem) {
        // Update existing item instead of adding duplicate
        setFormData({
          ...formData,
          line_items: formData.line_items.map(item => 
            item.id === existingItem.id 
              ? {
                  ...item,
                  quantity: newLineItem.quantity,
                  rate: newLineItem.rate,
                  amount: calculateLineItemAmount(newLineItem.quantity, newLineItem.rate)
                }
              : item
          )
        });
      } else {
        // Add new line item
        const finalLineItem = {
          ...newLineItem,
          id: Date.now().toString(),
          amount: calculateLineItemAmount(newLineItem.quantity, newLineItem.rate)
        };
        
        setFormData({
          ...formData,
          line_items: [...formData.line_items, finalLineItem]
        });
      }
      
      // Reset newLineItem form
      setNewLineItem({
        service_id: '',
        service_name: '',
        description: '',
        deliverables: [],
        quantity: 1,
        rate: 0,
        amount: 0
      });
    }
  };

  const removeLineItem = (id) => {
    setFormData({
      ...formData,
      line_items: formData.line_items.filter(item => item.id !== id)
    });
  };

  const updateLineItem = (id, field, value) => {
    setFormData({
      ...formData,
      line_items: formData.line_items.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updated.amount = calculateLineItemAmount(
              field === 'quantity' ? value : item.quantity,
              field === 'rate' ? value : item.rate
            );
          }
          return updated;
        }
        return item;
      })
    });
  };

  const calculateSubtotal = () => {
    return formData.line_items.reduce((sum, item) => sum + item.amount, 0);
  };

  // Calculate auto discount from pricing rules
  const calculateAutoDiscount = () => {
    const subtotal = calculateSubtotal();
    if (formData.discount_type === 'percentage') {
      return (subtotal * parseFloat(formData.discount_value || 0)) / 100;
    } else if (formData.discount_type === 'fixed') {
      return parseFloat(formData.discount_value || 0);
    }
    return 0;
  };

  // Calculate additional manual discount
  const calculateAdditionalDiscount = () => {
    const subtotalAfterAutoDiscount = calculateSubtotal() - calculateAutoDiscount();
    if (additionalDiscount.type === 'percentage') {
      return (subtotalAfterAutoDiscount * parseFloat(additionalDiscount.value || 0)) / 100;
    } else if (additionalDiscount.type === 'fixed') {
      return parseFloat(additionalDiscount.value || 0);
    }
    return 0;
  };

  // Total discount (auto + additional)
  const calculateDiscount = () => {
    return calculateAutoDiscount() + calculateAdditionalDiscount();
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.line_items.length === 0) {
      alert('Please add at least one line item');
      return;
    }

    setLoading(true);
    
    // Calculate total discount (auto + additional) to send to backend
    const totalDiscountAmount = calculateDiscount();
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/proposals`, {
        lead_id: lead.id,
        line_items: formData.line_items,
        // Send combined discount as fixed amount for simplicity
        discount_type: totalDiscountAmount > 0 ? 'fixed' : 'none',
        discount_value: totalDiscountAmount,
        // Also send breakdown for audit purposes
        auto_discount_type: formData.discount_type,
        auto_discount_value: parseFloat(formData.discount_value) || 0,
        additional_discount_type: additionalDiscount.type,
        additional_discount_value: parseFloat(additionalDiscount.value) || 0,
        terms_and_conditions: formData.terms_and_conditions
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      onSuccess(response.data);
      onClose();
    } catch (error) {
      console.error('Error creating proposal:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create proposal';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Create Proposal</h2>
            <p className="text-sm text-gray-600">For: {lead.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Lead Details Section */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Client Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="font-medium text-gray-700">Name:</span>
                <span className="ml-2 text-gray-900">{lead.name}</span>
              </div>
              {lead.phone && (
                <div>
                  <span className="font-medium text-gray-700">Phone:</span>
                  <span className="ml-2 text-gray-900">{lead.phone}</span>
                </div>
              )}
              {lead.email && (
                <div>
                  <span className="font-medium text-gray-700">Email:</span>
                  <span className="ml-2 text-gray-900">{lead.email}</span>
                </div>
              )}
              {lead.service_type && (
                <div>
                  <span className="font-medium text-gray-700">Service Type:</span>
                  <span className="ml-2 text-gray-900">{lead.service_type}</span>
                </div>
              )}
              {(lead.display_country || lead.destination_country) && (
                <div>
                  <span className="font-medium text-gray-700">Destination:</span>
                  <span className="ml-2 text-gray-900">{lead.display_country || lead.destination_country}</span>
                </div>
              )}
              {lead.travel_date && (
                <div>
                  <span className="font-medium text-gray-700">Travel Date:</span>
                  <span className="ml-2 text-gray-900">{lead.travel_date}</span>
                </div>
              )}
              {lead.num_applicants && (
                <div>
                  <span className="font-medium text-gray-700">Applicants:</span>
                  <span className="ml-2 text-gray-900">{lead.num_applicants}</span>
                </div>
              )}
              {lead.budget_indicated && (
                <div>
                  <span className="font-medium text-gray-700">Budget:</span>
                  <span className="ml-2 text-gray-900">₹{lead.budget_indicated}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Pricing from Rules */}
          {loadingPricing ? (
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <p className="text-gray-600">Loading pricing...</p>
            </div>
          ) : pricingSuggestion ? (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border-2 border-green-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Suggested Pricing (from Rules)</h3>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <p className="text-sm text-gray-600">Service</p>
                  <p className="font-semibold">{lead.service_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Applicants</p>
                  <p className="font-semibold">{lead.num_applicants || 1}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Rate per Applicant</p>
                  <p className="font-semibold text-green-600">₹{(pricingSuggestion.visaasure_charges_total / (lead.num_applicants || 1)).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="font-semibold text-green-700 text-xl">₹{pricingSuggestion.visaasure_charges_total.toLocaleString('en-IN')}</p>
                </div>
              </div>
              
              {/* Bulk discount button/message */}
              {!discountApplied && (lead.num_applicants || 1) > 1 && (
                <div className="mt-3">
                  {pricingSuggestion.discount_eligible ? (
                    <button
                      type="button"
                      onClick={handleApplyDiscount}
                      className="w-full bg-yellow-400 hover:bg-yellow-500 text-yellow-900 font-bold py-2 px-4 rounded-lg"
                    >
                      🎁 Apply Bulk Discount
                    </button>
                  ) : (
                    <div className="bg-white border border-green-300 text-green-800 py-2 px-4 rounded-lg text-center font-semibold">
                      ⭐ This is already our best quote!
                    </div>
                  )}
                </div>
              )}
              
              {discountApplied && pricingSuggestion.discount_applied && (
                <div className="mt-3 bg-green-100 border border-green-400 text-green-800 py-2 px-4 rounded-lg">
                  🎉 <strong>{pricingSuggestion.discount_applied}% Bulk Discount Applied!</strong>
                  <div className="text-sm mt-1">Discount of ₹{pricingSuggestion.discount_amount?.toLocaleString('en-IN')} will be shown separately in proposal</div>
                </div>
              )}
              
            </div>
          ) : null}

          {/* Line Items List */}
          {formData.line_items.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Proposal Items</h3>
              <div className="space-y-3">
                {formData.line_items.map(item => (
                  <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="mb-2">
                      <h4 className="font-semibold text-gray-800">{item.service_name}</h4>
                      {item.description && <p className="text-sm text-gray-600">{item.description}</p>}
                      {item.deliverables && item.deliverables.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-gray-700">Deliverables:</p>
                          <ul className="list-disc list-inside text-xs text-gray-600">
                            {item.deliverables.map((d, i) => <li key={i}>{d}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <label className="text-xs text-gray-500">Qty</label>
                        <div className="px-2 py-1 bg-gray-100 rounded font-semibold">
                          {item.quantity}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Rate</label>
                        <div className="px-2 py-1 bg-gray-100 rounded font-semibold">
                          ₹{item.rate.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Amount</label>
                        <div className="px-2 py-1 bg-gray-100 rounded font-semibold">
                          ₹{item.amount.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Discount Display (if applied from pricing rules) */}
          {formData.discount_type !== 'none' && formData.discount_value > 0 && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">✓ Auto Discount Applied</h3>
              <div className="text-sm">
                <p className="text-gray-700">
                  <span className="font-medium">Type:</span> {formData.discount_type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                </p>
                <p className="text-gray-700">
                  <span className="font-medium">Value:</span> {formData.discount_type === 'percentage' ? `${formData.discount_value}%` : `₹${formData.discount_value.toLocaleString('en-IN')}`}
                </p>
                <p className="text-gray-700">
                  <span className="font-medium">Amount:</span> - ₹{calculateAutoDiscount().toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-2">💡 This discount was applied from the pricing rules</p>
              </div>
            </div>
          )}

          {/* Additional Discount - Available for all users */}
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">🏷️ Add Additional Discount</h3>
            <p className="text-xs text-gray-500 mb-3">Apply an extra discount on top of any auto-applied pricing rule discount</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                <select
                  value={additionalDiscount.type}
                  onChange={(e) => setAdditionalDiscount(prev => ({ ...prev, type: e.target.value, value: e.target.value === 'none' ? 0 : prev.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="none">No Additional Discount</option>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                </select>
              </div>
              {additionalDiscount.type !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {additionalDiscount.type === 'percentage' ? 'Percentage' : 'Amount'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={additionalDiscount.type === 'percentage' ? 100 : undefined}
                    value={additionalDiscount.value}
                    onChange={(e) => setAdditionalDiscount(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder={additionalDiscount.type === 'percentage' ? 'e.g. 5' : 'e.g. 500'}
                  />
                </div>
              )}
            </div>
            {additionalDiscount.type !== 'none' && additionalDiscount.value > 0 && (
              <div className="mt-3 text-sm text-orange-700 font-medium">
                Additional Discount: - ₹{calculateAdditionalDiscount().toFixed(2)}
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="space-y-2 text-right">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-semibold">₹{calculateSubtotal().toFixed(2)}</span>
              </div>
              {formData.discount_type !== 'none' && formData.discount_value > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Auto Discount (Pricing Rules):</span>
                  <span className="font-semibold">- ₹{calculateAutoDiscount().toFixed(2)}</span>
                </div>
              )}
              {additionalDiscount.type !== 'none' && additionalDiscount.value > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>Additional Discount:</span>
                  <span className="font-semibold">- ₹{calculateAdditionalDiscount().toFixed(2)}</span>
                </div>
              )}
              {calculateDiscount() > 0 && (
                <div className="flex justify-between text-sm text-purple-600 font-medium">
                  <span>Total Discount:</span>
                  <span className="font-semibold">- ₹{calculateDiscount().toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-gray-800 pt-2 border-t border-blue-200">
                <span>Total:</span>
                <span>₹{calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
            <textarea
              value={formData.terms_and_conditions}
              onChange={(e) => setFormData({ ...formData, terms_and_conditions: e.target.value })}
              rows="6"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter terms and conditions..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || formData.line_items.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save size={20} />
              {loading ? 'Creating...' : 'Create Proposal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
